import {
  useDeleteSupplier,
  useDuplicateSupplier,
  useUpdateSupplier,
} from '@/api/supplier.api';
import { PATHS } from '@/constants/Paths';
import { buildSuppliersListSearch } from '@/utils/searchParams/buildSuppliersListSearch';
import {
  SupplierFormData,
  SupplierSchema,
} from '@/types/suppliers/SupplierFormData';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { zodResolver } from '@hookform/resolvers/zod';
import { startTransition, useEffect, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import {
  COUNTRIES,
  COUNTRIES_DATA,
  CURRENCIES,
} from '../../../../shared/CountriesData';
import StyledButton from '@/components/styled/StyledButton';
import HorizontalLine from '@/components/ui/HorizontalLine';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledInput from '@/components/styled/StyledInput';
import MarginRateStyledInput from '@/components/styled/MarginRateStyledInput';
import ContactForm from '@/components/suppliers/ContactForm';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import clsx from 'clsx';
import ModalDeletion from '@/components/ui/ModalDeletion';
import ModalDuplication from '@/components/ui/ModalDuplication';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledFormSection from '@/components/styled/StyledFormSection';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import { UpdateSupplierData } from '@/types/suppliers/UpdateSupplierData';
import { useToast } from '@/hooks/useToast';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { SUPPLIER_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';
import { formatSupplierCode } from '@/utils/displayCodes';

type SubmitFormType = (isDraft?: boolean) => SubmitHandler<SupplierFormData>;

const ViewSupplierForm = ({
  supplierResponse,
}: {
  supplierResponse: UpdateSupplierData;
}) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { mutateAsync: updateSupplierData, error: updateSupplierError } =
    useUpdateSupplier();
  const { mutateAsync: deleteSupplier } = useDeleteSupplier();
  const { mutateAsync: duplicateSupplier } = useDuplicateSupplier();

  const navigate = useNavigate();
  const location = useLocation();
  const [contactsIds, setContactsIds] = useState<string[]>([]);
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const submitError = updateSupplierError
    ? t('suppliers.errorCorrection')
    : null;

  const methods = useForm<SupplierFormData>({
    resolver: zodResolver(SupplierSchema),
    mode: 'onChange',
    defaultValues: {
      address: supplierResponse?.address ?? undefined,
      contacts:
        supplierResponse.contacts && supplierResponse.contacts.length > 0
          ? supplierResponse?.contacts.map((contact) => ({
              name: contact.name ?? undefined,
              email: contact.email ?? undefined,
              phoneNumber: contact.phoneNumber ?? undefined,
            }))
          : [{ name: undefined, email: undefined, phoneNumber: undefined }],
      currency: {
        value: supplierResponse?.currency,
        label: CURRENCIES.find(
          (currency) => currency.value === supplierResponse?.currency,
        )?.label,
      },
      marginRate: supplierResponse?.marginRate ?? undefined,
      name: supplierResponse?.name ?? undefined,
      country: {
        label: supplierResponse?.country,
        value: supplierResponse?.country,
      },
      city: supplierResponse?.city ?? undefined,
      postalCode: supplierResponse?.postalCode ?? undefined,
    },
    shouldUnregister: true,
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
    reset,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
    shouldUnregister: true,
  });

  const selectedCountry = useWatch({
    control,
    name: 'country',
    defaultValue: {
      label: supplierResponse.country ?? '',
      value: supplierResponse.country ?? '',
    },
  });
  const currentName = useWatch({
    control,
    name: 'name',
    defaultValue: supplierResponse.name,
  });

  const changeCountryWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'country',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const countryData = COUNTRIES_DATA.find(
        (data) => data.Pays === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.label === countryData?.Devise,
      ) ?? { value: '', label: '' };
      return currency;
    },
  });

  const hasEnabledEditRef = useRef(false);
  const prevHasErrorRef = useRef(false);

  useEffect(() => {
    const hasError = !!updateSupplierError;

    if (hasError && !prevHasErrorRef.current && !hasEnabledEditRef.current) {
      startTransition(() => {
        setIsEditEnabled(true);
      });
      hasEnabledEditRef.current = true;
    }

    // Reset flag when error clears
    if (!hasError && prevHasErrorRef.current) {
      hasEnabledEditRef.current = false;
    }

    prevHasErrorRef.current = hasError;
  }, [updateSupplierError]);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      const supplier = await toastAsync(
        updateSupplierData({
          supplierId: supplierResponse.id ?? '',
          supplierData: {
            address: data.address,
            contacts: data.contacts.map((contact, index) => ({
              ...contact,
              id: contactsIds[index] ?? undefined,
            })),
            currency: data.currency.value,
            marginRate: parsedMarginRate.success
              ? parsedMarginRate.data
              : undefined,
            name: data.name,
            country: data.country.value,
            city: data.city,
            postalCode: data.postalCode,
            status: isDraft ? undefined : SupplierStatus.PUBLISHED,
          },
        }),
        { success: t('suppliers.updateSuccess') },
      );
      const supplierData = supplier.data;
      setContactsIds(
        supplierData.contacts.map((contact: { id: string }) => contact.id),
      );
      setIsEditEnabled(false);
      if (!isDraft)
        navigate(
          PATHS.home.suppliers.list +
            buildSuppliersListSearch(location.state?.listSearchParams),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('suppliers.newSupplier')}
          code={formatSupplierCode(supplierResponse.code)}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('suppliers.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.suppliers.list +
                    buildSuppliersListSearch(location.state?.listSearchParams),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('suppliers.delete')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
            onClick={() => setIsDeletionModalOpen(true)}
          />
          <StyledButton
            variant="outlined"
            title={t('suppliers.duplicate')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setIsDuplicationModalOpen(true)}
            icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
          />
          {!isEditEnabled ? (
            <StyledButton
              title={t('suppliers.edit')}
              onClick={() => setIsEditEnabled(true)}
              className="font-semibold gap-1 px-3 py-2"
              icon={<EditIcon className="w-5 h-5 stroke-white" />}
            />
          ) : (
            <>
              <StyledButton
                title={t('suppliers.cancel')}
                onClick={() => {
                  setIsEditEnabled(false);
                  reset();
                }}
                className="font-semibold gap-1 px-3 py-2"
              />
              <StyledButtonLoader
                title={t('suppliers.save')}
                disabled={
                  supplierResponse.status === SupplierStatus.DRAFT
                    ? !currentName || !selectedCountry.value
                    : !isValid
                }
                successTitle={t('suppliers.draftSaved')}
                onClick={() => {
                  submitForm(true)(getValues());
                  setIsEditEnabled(false);
                }}
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </>
          )}
        </StyledFormHeaderWithBreadcrumb>
        {submitError && (
          <p className="p-6 text-sm leading-6 font-medium text-error-500">
            {submitError}
          </p>
        )}
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.requiredFields')}>
          <StyledInput
            control={control}
            name="name"
            title={t('suppliers.supplierName')}
            placeholder={t('suppliers.exampleName')}
            disabled={!isEditEnabled}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              options={COUNTRIES}
              control={control}
              name="country"
              title={t('suppliers.country')}
              placeholder={t('suppliers.selectCountry')}
              disabled={!isEditEnabled}
              className="w-3/5"
              onChange={changeCountryWithCurrency}
            />
            <StyledSelect
              options={CURRENCIES}
              control={control}
              name="currency"
              title={t('suppliers.mainCurrency')}
              disabled={!isEditEnabled}
              placeholder={t('suppliers.selectCurrency')}
              className="w-2/5"
            />
          </div>
        </StyledFormSection>
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.administrativeInfo')}>
          <StyledInput
            control={control}
            name="address"
            title={t('suppliers.address')}
            disabled={!isEditEnabled}
            placeholder={t('suppliers.addAddress')}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledInput
              control={control}
              name="city"
              title={t('suppliers.city')}
              disabled={!isEditEnabled}
              placeholder={t('suppliers.addCity')}
            />
            <StyledInput
              control={control}
              name="postalCode"
              type="number"
              disabled={!isEditEnabled}
              title={t('suppliers.postalCode')}
              placeholder={t('suppliers.addPostalCode')}
              hideNumberFieldArrows
            />
          </div>
          {fields.map((field, index) => (
            <ContactForm
              key={field.id}
              index={index}
              control={control}
              onRemoveContact={() => remove(index)}
              disabled={!isEditEnabled}
            />
          ))}
          {isEditEnabled && (
            <StyledButton
              title={t('suppliers.addContact')}
              className="text-brand-600 w-fit p-0"
              variant="link"
              icon={<PlusIcon className="w-5 h-5 fill-brand-600" />}
              onClick={() =>
                append({
                  name: undefined,
                  email: undefined,
                  phoneNumber: undefined,
                })
              }
            />
          )}
        </StyledFormSection>
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.marginRate')}>
          <MarginRateStyledInput
            control={control}
            name="marginRate"
            title={getTranslatedConst(SUPPLIER_MARGIN_RATE_LABEL)}
            placeholder={t('suppliers.addRate')}
            disabled={!isEditEnabled}
          />
        </StyledFormSection>
        <HorizontalLine />
        {supplierResponse?.status === SupplierStatus.DRAFT && (
          <div className="flex flex-row-reverse p-6">
            <StyledButton
              title={t('suppliers.publishSupplier')}
              className="font-semibold gap-1 px-3 py-2"
              onClick={handleSubmit(submitForm(false))}
              disabled={!isValid}
              icon={
                <PlusIcon
                  className={clsx('w-5 h-5', {
                    'fill-white': isValid,
                    'fill-gray-400': !isValid,
                  })}
                />
              }
            />
          </div>
        )}
        {isDeletionModalOpen && (
          <ModalDeletion
            closeModal={() => {
              setIsDeletionModalOpen(false);
            }}
            actionOnClick={() => {
              if (supplierResponse.id)
                deleteSupplier({ supplierId: supplierResponse.id });
              navigate(
                PATHS.home.suppliers.list +
                  buildSuppliersListSearch(location.state?.listSearchParams),
              );
            }}
            type="fournisseur"
          />
        )}
        {isDuplicationModalOpen && (
          <ModalDuplication
            actionOnClick={async (selectedFields) => {
              if (supplierResponse.id) {
                const duplicatedSupplier = await duplicateSupplier({
                  supplierId: supplierResponse.id,
                  fields: selectedFields.flatMap((fields) => fields.split(';')),
                });
                navigate(
                  PATHS.home.suppliers.list + `/${duplicatedSupplier.data.id}`,
                  { state: location.state },
                );
              }
            }}
            closeModal={() => {
              setIsDuplicationModalOpen(false);
            }}
            fields={[
              {
                label: t('suppliers.duplicationFields.requiredFields'),
                value: 'name;country;currency',
              },
              {
                label: t('suppliers.duplicationFields.administrativeInfo'),
                value: 'address;city;postalCode',
              },
              {
                label: t('suppliers.duplicationFields.contacts'),
                value: 'contacts',
              },
              {
                label: t('suppliers.duplicationFields.marginRate'),
                value: 'marginRate',
              },
            ]}
            code={supplierResponse.code}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default ViewSupplierForm;
